import { useEffect, useState } from "react";
import { BrandLockup } from "./BrandLockup";
import { ConferenceReviewSection, ReviewTabs, type ReviewSection } from "./ConferenceReview";
import { LearningRecordDocument } from "./LearningRecord";
import {
  demoConferenceEvidence,
  demoConferenceValues,
  demoSessions,
  type DemoSession,
} from "./learning-companion-demo-data";
import { Link } from "./router";

type DemoStage = "journey" | "inputs" | "record" | "library" | "wrap";

const stages: readonly { id: DemoStage; label: string; shortLabel: string }[] = [
  { id: "journey", label: "Meet the teacher", shortLabel: "Teacher" },
  { id: "inputs", label: "What the app gathers", shortLabel: "Sources" },
  { id: "record", label: "One session record", shortLabel: "Record" },
  { id: "library", label: "Six saved sessions", shortLabel: "Sessions" },
  { id: "wrap", label: "Convention Wrap-Up", shortLabel: "Wrap-Up" },
];

const libraryTotals = demoSessions.reduce((total, session) => ({
  ideas: total.ideas + session.values.mainPoints.length,
  frameworks: total.frameworks + session.values.frameworks.length,
  moves: total.moves + session.values.practicalStrategies.length,
}), { ideas: 0, frameworks: 0, moves: 0 });

export function LearningCompanionDemo() {
  const [stage, setStage] = useState<DemoStage>("journey");
  const [sessionId, setSessionId] = useState(demoSessions[0]!.id);
  const [reviewSection, setReviewSection] = useState<ReviewSection>("direction");
  const session = demoSessions.find((candidate) => candidate.id === sessionId) ?? demoSessions[0]!;
  const stageIndex = stages.findIndex((candidate) => candidate.id === stage);

  useEffect(() => { document.title = "Learning Companion demo · WVCEA Connect"; }, []);
  useEffect(() => {
    document.querySelector<HTMLElement>("[data-demo-stage-heading]")?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [stage]);

  const move = (nextStage: DemoStage) => setStage(nextStage);

  return (
    <div className="learning-demo-shell">
      <a className="skip-link" href="#demo-main">Skip to the demo</a>
      <header className="learning-demo-topbar">
        <Link href="/" aria-label="WVCEA Connect home"><BrandLockup size="small" className="brand-lockup-header" /></Link>
        <div><strong>Learning Companion demo</strong><span>Six sessions · one teacher's private record</span></div>
        <Link href="/">Exit demo</Link>
      </header>

      <main id="demo-main" className="learning-demo-main">
        <header className="learning-demo-intro">
          <div>
            <span className="learning-demo-badge">Guided walk-through</span>
            <h1>See what one teacher carries home.</h1>
            <p>Follow Emily Carter from presenter files and session recordings to six useful Learning Records and one Convention Wrap-Up.</p>
          </div>
          <aside><strong>About 8 minutes</strong><span>Move through the five steps at your own pace.</span></aside>
        </header>

        <DemoStageNav current={stage} onSelect={move} />

        <section className="learning-demo-stage" aria-live="polite">
          {stage === "journey" && <TeacherJourney onNext={() => move("inputs")} />}
          {stage === "inputs" && <DemoInputs session={session} onSessionChange={setSessionId} />}
          {stage === "record" && <DemoRecord session={session} onSessionChange={setSessionId} />}
          {stage === "library" && <DemoLibrary onOpen={(id) => { setSessionId(id); move("record"); }} />}
          {stage === "wrap" && <DemoWrap section={reviewSection} onSectionChange={setReviewSection} />}
        </section>

        <DemoPager
          stageIndex={stageIndex}
          onBack={stageIndex > 0 ? () => move(stages[stageIndex - 1]!.id) : null}
          onNext={stageIndex < stages.length - 1 ? () => move(stages[stageIndex + 1]!.id) : null}
        />

        <p className="learning-demo-disclosure">This walk-through uses made-up names and session materials. No real attendee information appears here.</p>
      </main>
    </div>
  );
}

function DemoStageNav({ current, onSelect }: { current: DemoStage; onSelect: (stage: DemoStage) => void }) {
  return (
    <nav className="learning-demo-steps" aria-label="Demo steps">
      {stages.map((stage, index) => (
        <button key={stage.id} type="button" className={current === stage.id ? "active" : ""} aria-current={current === stage.id ? "step" : undefined} onClick={() => onSelect(stage.id)}>
          <span>{index + 1}</span><strong>{stage.shortLabel}</strong><small>{stage.label}</small>
        </button>
      ))}
    </nav>
  );
}

function TeacherJourney({ onNext }: { onNext: () => void }) {
  return (
    <div className="learning-demo-journey">
      <header className="learning-demo-stage-heading">
        <p>Emily's convention</p>
        <h2 data-demo-stage-heading tabIndex={-1}>Six sessions. One useful record of what she learned.</h2>
        <span>Emily teaches fourth grade at Valley Christian School. She wants practical help with student independence, discussion, and revision.</span>
      </header>

      <div className="learning-demo-teacher">
        <div className="learning-demo-monogram" aria-hidden="true">EC</div>
        <div><strong>Emily Carter</strong><span>Fourth-grade teacher · 11 years in the classroom</span><p>“I do not need another folder of slides. I need to remember what mattered and know what I can try on Monday.”</p></div>
      </div>

      <ol className="learning-demo-timeline">
        {demoSessions.map((session, index) => (
          <li key={session.id}>
            <span>{index + 1}</span>
            <div><small>{session.day.replace(", September", " · Sept.")} · {session.time}</small><strong>{session.title}</strong><p>{session.values.centralIdea}</p></div>
          </li>
        ))}
      </ol>

      <div className="learning-demo-callout">
        <strong>The app does the collecting. Emily stays in charge of the result.</strong>
        <p>The app writes a first version. Emily can change it, remove it, or leave a section blank before she saves anything.</p>
        <button className="learning-demo-primary" type="button" onClick={onNext}>See what goes into one record <span aria-hidden="true">→</span></button>
      </div>
    </div>
  );
}

function DemoInputs({ session, onSessionChange }: { session: DemoSession; onSessionChange: (id: string) => void }) {
  return (
    <div>
      <header className="learning-demo-stage-heading">
        <p>What Emily saved</p>
        <h2 data-demo-stage-heading tabIndex={-1}>The app brings the session materials together.</h2>
        <span>Emily has presenter files, her recording transcript, and her own notes. PowerPoints become ordered PDFs while the original files remain available.</span>
      </header>
      <DemoSessionPicker session={session} onChange={onSessionChange} />

      <div className="learning-demo-source-summary">
        <section>
          <header><span aria-hidden="true">P</span><div><small>Presenter supplied</small><h3>{session.materials.length} {session.materials.length === 1 ? "file" : "files"}</h3></div></header>
          <ul>{session.materials.map((material) => <li key={material.name}><strong>{material.name}</strong><span>{material.detail}</span></li>)}</ul>
          <details><summary>Read the official session description</summary><p>{session.description}</p></details>
        </section>
        <section>
          <header><span aria-hidden="true">R</span><div><small>Emily's recording</small><h3>{session.recording.duration}</h3></div></header>
          <dl><div><dt>Transcript</dt><dd>{session.recording.wordCount}</dd></div><div><dt>Voices</dt><dd>{session.recording.speakers}</dd></div></dl>
          <details><summary>Read a transcript excerpt</summary><blockquote>“{session.recording.excerpt}”</blockquote></details>
        </section>
        <section>
          <header><span aria-hidden="true">N</span><div><small>Emily added</small><h3>{session.notes.markers.length} marked {session.notes.markers.length === 1 ? "moment" : "moments"}</h3></div></header>
          <p>{session.notes.typed}</p>
          <details><summary>Read her markers and next step</summary><ul>{session.notes.markers.map((marker) => <li key={marker}>{marker}</li>)}</ul><p><strong>Question:</strong> {session.notes.question}</p><p><strong>Try next:</strong> {session.notes.nextStep}</p></details>
        </section>
      </div>

      <div className="learning-demo-privacy-note"><strong>What leaves the phone?</strong><span>The transcript text can be sent to help write the record. The audio stays on the device. Emily's record remains private from association staff.</span></div>
    </div>
  );
}

function DemoRecord({ session, onSessionChange }: { session: DemoSession; onSessionChange: (id: string) => void }) {
  return (
    <div>
      <header className="learning-demo-stage-heading">
        <p>Emily's session record</p>
        <h2 data-demo-stage-heading tabIndex={-1}>A session document with enough substance to use.</h2>
        <span>The app uses the same sections for every session. Emily read the wording, made changes, and saved what she wanted to keep.</span>
      </header>
      <DemoSessionPicker session={session} onChange={onSessionChange} />
      <div className="learning-demo-record-context"><div><small>{session.day} · {session.time}</small><h3>{session.title}</h3><span>{session.presenter} · {session.room}</span></div><span>Read and saved by Emily</span></div>
      <LearningRecordDocument values={session.values} sources={session.sources} />
    </div>
  );
}

function DemoLibrary({ onOpen }: { onOpen: (id: string) => void }) {
  return (
    <div>
      <header className="learning-demo-stage-heading">
        <p>Emily's Learning Companion</p>
        <h2 data-demo-stage-heading tabIndex={-1}>All six records stay together.</h2>
        <span>She can return to a session, edit what she saved, print it, or bring all six records into her Convention Wrap-Up.</span>
      </header>
      <div className="learning-demo-library-summary"><strong>6 of 6 session records finished</strong><span>{libraryTotals.ideas} core ideas · {libraryTotals.frameworks} routines · {libraryTotals.moves} ideas to try</span></div>
      <div className="learning-demo-session-list">
        {demoSessions.map((session, index) => (
          <article key={session.id}>
            <span>{index + 1}</span>
            <div><small>{session.day.replace(", September", " · Sept.")} · {session.time}</small><h3>{session.title}</h3><p>{session.values.centralIdea}</p><em>{session.materials.length} presenter {session.materials.length === 1 ? "file" : "files"} · recording transcript · Emily's notes</em></div>
            <button type="button" onClick={() => onOpen(session.id)} aria-label={`Open the saved record for ${session.title}`}>Open record <b aria-hidden="true">›</b></button>
          </article>
        ))}
      </div>
    </div>
  );
}

function DemoWrap({ section, onSectionChange }: { section: ReviewSection; onSectionChange: (section: ReviewSection) => void }) {
  return (
    <div>
      <header className="learning-demo-stage-heading">
        <p>Emily's Convention Wrap-Up</p>
        <h2 data-demo-stage-heading tabIndex={-1}>The six records become one Convention Wrap-Up.</h2>
        <span>The app brings her six finished session records together. Emily can change or remove anything before she saves it.</span>
      </header>
      <div className="learning-demo-wrap-hero">
        <div><span>Emily Carter's private record</span><h3>2026 Educators Convention</h3><p>The ideas, questions, and next steps she chose to keep.</p></div>
        <strong>6 finished session records</strong>
      </div>
      <ReviewTabs selected={section} onSelect={onSectionChange} />
      <section className="conference-review-workspace learning-demo-review-workspace" id="conference-review-panel" role="tabpanel" aria-labelledby={`conference-review-tab-${section}`}>
        <ConferenceReviewSection section={section} values={demoConferenceValues} evidence={demoConferenceEvidence} />
      </section>
      <section className="learning-demo-wrap-sources">
        <h3>Made from Emily's finished session records</h3>
        <ul>{demoSessions.map((session) => <li key={session.id}><span aria-hidden="true">✓</span><div><strong>{session.title}</strong><small>Presenter files · transcript · personal notes</small></div></li>)}</ul>
      </section>
    </div>
  );
}

function DemoSessionPicker({ session, onChange }: { session: DemoSession; onChange: (id: string) => void }) {
  return (
    <label className="learning-demo-picker">
      <span>Session shown</span>
      <select value={session.id} onChange={(event) => onChange(event.target.value)}>
        {demoSessions.map((candidate, index) => <option key={candidate.id} value={candidate.id}>{index + 1}. {candidate.title}</option>)}
      </select>
    </label>
  );
}

function DemoPager({ stageIndex, onBack, onNext }: { stageIndex: number; onBack: (() => void) | null; onNext: (() => void) | null }) {
  return (
    <footer className="learning-demo-pager">
      <span>Step {stageIndex + 1} of {stages.length}</span>
      <div>{onBack && <button type="button" onClick={onBack}>← Back</button>}{onNext && <button className="learning-demo-primary" type="button" onClick={onNext}>Next: {stages[stageIndex + 1]!.shortLabel} <span aria-hidden="true">→</span></button>}</div>
    </footer>
  );
}
